import React from 'react';

/**
 * SMART VIP RANK (God 2026-09-30): "Bots, Symbol aur TF ko unke Profit aur Win-rate ke hisaab se auto-rank kare
 * ... Rank 1 / best performers ko traffic rule me VIP pass (Tier-0)". Data: /reports/traffic -> vip_rank, built by
 * modules/vip_rank.py (last 5 trading days, >= 20 closed trades, ALL-INCLUSIVE net, win = net > 0; rank = mean of
 * the net-P&L rank and the win-rate rank; VIP = best 3 with POSITIVE net).
 *
 * What VIP means, exactly as God chose it: PRIORITY ONLY - its candle, brain decision and order go first and the
 * load governor never sheds it; the 30 ms traffic gate still applies. The panel says so, because a "VIP" badge
 * that read as "exempt from the jam rule" would be the wrong promise.
 */

export type VipRow = { bot: string; symbol: string; tf: string; trades: number; win_pct: number; net: number;
  score: number; rank: number; vip: boolean };
export type VipRank = { ts: string; days: string[]; window_days: number; min_trades: number; top_n: number;
  basis: string; ranked: VipRow[]; ineligible: number };

const BOT_NAME: Record<string, string> = {
  mai_veg: 'Vega', mai_veg_mcx: 'Vega Max', mai_shool: 'Shool', mai_shool_mcx: 'Shool Max',
  mai_sthambh: 'Sthambh', mai_sthambh_etf: 'Sthambh ETF', mai_veg_sail: 'Vega Sell', mai_veg_mcx_sail: 'Vega Max Sell',
};
const MEDAL = [
  { icon: '🥇', col: '#fbbf24', bg: 'rgba(251,191,36,.10)', bd: 'rgba(251,191,36,.45)' },
  { icon: '🥈', col: '#cbd5e1', bg: 'rgba(203,213,225,.08)', bd: 'rgba(203,213,225,.35)' },
  { icon: '🥉', col: '#f59e0b', bg: 'rgba(217,119,6,.10)', bd: 'rgba(217,119,6,.40)' },
];
const STATUS_COL: Record<string, string> = { PASS: '#10b981', FAIL: '#f59e0b', JAM: '#ef4444', IDLE: '#64748b' };
const rs = (v: number) => `${v < 0 ? '−' : '+'}₹${Math.abs(Math.round(v)).toLocaleString('en-IN')}`;

const VipRankPanel: React.FC<{ vip?: VipRank | null; statusOf: (symbol: string, tf: string) => string | undefined }> =
  ({ vip, statusOf }) => {
  if (!vip) {
    return (
      <div style={{ fontSize: 11, color: '#64748b', margin: '0 0 10px' }}>
        🏆 VIP rank abhi nahi aaya (API purana ho sakta hai, ya pehla hisaab chal raha hai).
      </div>
    );
  }
  const vips = vip.ranked.filter(r => r.vip);
  const th: React.CSSProperties = { padding: '3px 6px', color: '#64748b', fontWeight: 700, textAlign: 'left', whiteSpace: 'nowrap' };
  const td: React.CSSProperties = { padding: '3px 6px', whiteSpace: 'nowrap' };
  return (
    <div style={{ margin: '0 0 12px', border: '1px solid #1e293b', borderRadius: 10, padding: 10, background: '#0f172a' }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'baseline', marginBottom: 8 }}>
        <span style={{ fontWeight: 800, fontSize: 12, letterSpacing: '.06em', color: '#fbbf24' }}>🏆 SMART VIP RANK</span>
        <span style={{ fontSize: 10.5, color: '#94a3b8' }}>
          {vip.days[0]} → {vip.days[vip.days.length - 1]} · ≥{vip.min_trades} trade · net ALL-INCLUSIVE · rank =
          (net rank + win-rate rank) ÷ 2 · VIP = top {vip.top_n}, net &gt; 0 · in-sample
        </span>
        <span style={{ fontSize: 10, color: '#475569' }}>updated {vip.ts.slice(11, 16)} IST</span>
      </div>

      {vips.length > 0 ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 8, marginBottom: 10 }}>
          {vips.map((r, i) => {
            const m = MEDAL[i] || MEDAL[2];
            const st = statusOf(r.symbol, r.tf) || 'IDLE';
            return (
              <div key={`${r.bot}|${r.symbol}|${r.tf}`}
                style={{ border: `1px solid ${m.bd}`, background: m.bg, borderRadius: 10, padding: '8px 10px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: 18 }}>{m.icon}</span>
                  <span style={{ fontSize: 10, fontWeight: 800, color: '#0f172a', background: m.col, borderRadius: 4,
                                 padding: '1px 6px' }}>👑 VIP · TIER-0</span>
                </div>
                <div style={{ fontSize: 13, fontWeight: 800, color: '#e2e8f0', marginTop: 4 }}>
                  {r.symbol} {r.tf}
                </div>
                <div style={{ fontSize: 10.5, color: '#94a3b8' }}>{BOT_NAME[r.bot] || r.bot} · rank #{r.rank}</div>
                <div style={{ display: 'flex', gap: 10, marginTop: 6, fontSize: 11 }}>
                  <span style={{ color: r.net >= 0 ? '#10b981' : '#ef4444', fontWeight: 800 }}>{rs(r.net)}</span>
                  <span style={{ color: '#cbd5e1' }}>win {r.win_pct}%</span>
                  <span style={{ color: '#64748b' }}>{r.trades} tr</span>
                </div>
                <div style={{ marginTop: 6, fontSize: 10.5 }}>
                  traffic: <b style={{ color: STATUS_COL[st] || '#64748b' }}>{st}</b>
                  <span style={{ color: '#475569' }}>{st === 'FAIL' || st === 'JAM' ? ' · 30 ms gate abhi rok raha hai' : ''}</span>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 8 }}>
          Abhi koi VIP nahi — kisi eligible cell ka net P&amp;L positive nahi.
        </div>
      )}

      <div style={{ overflowX: 'auto' }}>
        <table style={{ borderCollapse: 'collapse', fontSize: 11, width: '100%', minWidth: 640 }}>
          <thead>
            <tr><th style={th}>RANK</th><th style={th}>BOT</th><th style={th}>SYMBOL × TF</th><th style={th}>TRADES</th>
                <th style={th}>WIN %</th><th style={th}>NET (all-incl)</th><th style={th}>SCORE</th><th style={th}>TRAFFIC</th>
                <th style={th}>PASS</th></tr>
          </thead>
          <tbody>
            {vip.ranked.map(r => {
              const st = statusOf(r.symbol, r.tf) || 'IDLE';
              return (
                <tr key={`${r.bot}|${r.symbol}|${r.tf}`} style={{ borderTop: '1px solid #1e293b',
                    background: r.vip ? 'rgba(251,191,36,.05)' : 'transparent' }}>
                  <td style={{ ...td, color: r.vip ? '#fbbf24' : '#94a3b8', fontWeight: 800 }}>#{r.rank}</td>
                  <td style={{ ...td, color: '#cbd5e1' }}>{BOT_NAME[r.bot] || r.bot}</td>
                  <td style={{ ...td, color: '#e2e8f0', fontWeight: 700 }}>{r.symbol} {r.tf}</td>
                  <td style={{ ...td, color: '#94a3b8' }}>{r.trades}</td>
                  <td style={{ ...td, color: '#cbd5e1' }}>{r.win_pct}%</td>
                  <td style={{ ...td, color: r.net >= 0 ? '#10b981' : '#ef4444', fontWeight: 700 }}>{rs(r.net)}</td>
                  <td style={{ ...td, color: '#64748b' }}>{r.score}</td>
                  <td style={{ ...td, color: STATUS_COL[st] || '#64748b', fontWeight: 700 }}>{st}</td>
                  <td style={td}>{r.vip
                    ? <span style={{ color: '#fbbf24', fontWeight: 800 }}>👑 VIP</span>
                    : <span style={{ color: '#475569' }}>{r.net > 0 ? 'rank bahar' : 'net < 0'}</span>}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div style={{ marginTop: 6, fontSize: 10, color: '#475569' }}>
        VIP = priority only: candle, brain aur order pehle; load governor kabhi nahi rokta. 30 ms traffic gate VIP par
        bhi laagu hai. {vip.ineligible} cell &lt; {vip.min_trades} trade ki wajah se rank me nahi.
      </div>
    </div>
  );
};

export default VipRankPanel;
